/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `SideMenu` for the Caintra Connect frontend.
 * @module components/SideMenu
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { KeyboardArrowLeft } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useNavigation } from "../context/NavigationContext";
import styles from "../styles/SideMenu.module.css";
import Caintra_Logo_02 from "../assets/Caintra_Logo_02.png";
import { ReactComponent as CaintraIcon } from "../assets/Caintra_Icon.svg";
import useSideMenu from "./SideMenu/useSideMenu";
import UserProfileDropdown from "./SideMenu/UserProfileDropdown";
import MenuNavigation from "./SideMenu/MenuNavigation";
import SideMenuBottom, { SideMenuFooter } from "./SideMenu/SideMenuBottom";
import { isMenuItemActive as checkMenuItemActive } from "./SideMenu/sideMenuUtils";

/**
 * Application sidebar: logo, user profile dropdown, navigation, and footer.
 * Logic lives in useSideMenu; presentation is split into focused sub-components.
 */
const SideMenu = ({ onMobileNavigation, isMobile = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { navigationState } = useNavigation();

  const {
    user,
    permissions,
    isCollapsed,
    isTablet,
    showUserDropdown,
    personalizarEventoExpanded,
    setPersonalizarEventoExpanded,
    toggleUserDropdown,
    toggleCollapse,
    onSignOut,
    navigateTo,
    closeDropdownAndNavigate,
    handleMyCompanyClick,
  } = useSideMenu({ onMobileNavigation });

  /** Delegates active-state checks to the shared utility with current route context. */
  const isMenuItemActive = (path, context) =>
    checkMenuItemActive(path, context, location, navigationState);

  const containerClass = `${styles.menuContainer} ${
    isCollapsed && !isMobile ? styles.collapsed : ""
  }`;

  return (
    <>
      <div className={containerClass}>
        <div className={styles.logoContainer}>
          <img
            className={styles.logoImage}
            src={Caintra_Logo_02}
            alt={t("common.caintraLogoAlt")}
          />
          <CaintraIcon className={styles.logoIcon} />
        </div>

        <UserProfileDropdown
          user={user}
          permissions={permissions}
          isCollapsed={isCollapsed}
          showUserDropdown={showUserDropdown}
          toggleUserDropdown={toggleUserDropdown}
          isMenuItemActive={isMenuItemActive}
          closeDropdownAndNavigate={closeDropdownAndNavigate}
          handleMyCompanyClick={handleMyCompanyClick}
        />

        <div className={styles.mainContent}>
          <MenuNavigation
            user={user}
            permissions={permissions}
            location={location}
            personalizarEventoExpanded={personalizarEventoExpanded}
            setPersonalizarEventoExpanded={setPersonalizarEventoExpanded}
            isMenuItemActive={isMenuItemActive}
            navigateTo={navigateTo}
          />

          <SideMenuBottom
            user={user}
            permissions={permissions}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            isMenuItemActive={isMenuItemActive}
            navigateTo={navigateTo}
            onSignOut={onSignOut}
          />
        </div>

        <SideMenuFooter />

        <div
          className={`${styles.collapseButton} ${
            isCollapsed ? styles.collapsed : ""
          }`}
          onClick={toggleCollapse}
        >
          <KeyboardArrowLeft sx={{ color: "white", fontSize: 16 }} />
        </div>
      </div>

      {isTablet && !isCollapsed && (
        <div
          className={`${styles.tabletBackgroundOverlay} ${styles.overlayVisible}`}
          onClick={toggleCollapse}
        />
      )}
    </>
  );
};

export default SideMenu;
